import React, { FC } from 'react';
import { FilterModel } from '../../../common/model/ui/filter.model';
import { UserInfoFields } from '../../../firebase/document-field.enums';
import OvListFilter from './OvListFilter';
import { FilterType } from '../../../common/model/ui/filter.type';
import { hasAppliedListFilters } from '../../../common/utils/helpers';
import { useQuery } from '@tanstack/react-query';
import UserService from '../../../services/user.service';
import { useTranslation } from 'react-i18next';
import { ClinicLocationService } from '../../../services/clinic-location.service';

interface OvClinicLocationPatientSearchFilterProps {
  clinicLocationId?: string;
  removeFilters: () => void;
  onHandleFilterValueChange: (filter: FilterModel) => void;
  filterValues: {
    providerId: string;
    knownReproductiveDisorder: string;
    shouldHideNotRegisteredUsers: string;
  };
}

const OvClinicLocationPatientSearchFilter: FC<
  OvClinicLocationPatientSearchFilterProps
> = ({
  clinicLocationId,
  removeFilters,
  onHandleFilterValueChange,
  filterValues,
}) => {
  const { t } = useTranslation();

  const filters: FilterModel[] = [
    {
      fieldName: UserInfoFields.provider_id,
      label: 'common.userFields.provider',
      type: FilterType.select,
      options: [],
      value: filterValues.providerId,
    },
    {
      fieldName: UserInfoFields.should_hide_not_registered_users,
      label: 'common.userSearchFilters.shouldHideNotRegisteredUsers',
      type: FilterType.radio,
      options: [
        {
          label: 'common.actions.yes',
          value: true,
        },
        {
          label: 'common.actions.no',
          value: false,
        },
        {
          label: 'common.actions.notSelected',
          value: '',
        },
      ],
      value: filterValues.shouldHideNotRegisteredUsers,
    },
    {
      fieldName: UserInfoFields.known_reproductive_disorder,
      label: 'common.userFields.fertilityDisorder',
      type: FilterType.select,
      options: [],
      value: filterValues.knownReproductiveDisorder,
    },
  ];

  const { data: disorders } = useQuery({
    queryKey: ['admin-users-fertility-search-filters', clinicLocationId],
    queryFn: () => UserService.loadAllFertilityDisorders(),
    retry: 0,
  });

  const mappedDisorders =
    disorders?.map((disorder) => disorder.known_reproductive_disorder) ?? [];

  const disordersFilter = filters.find(
    (filter) => filter.fieldName === UserInfoFields.known_reproductive_disorder
  );

  disordersFilter?.options?.push({
    label: t('common.values.none'),
    value: 'None',
  });

  mappedDisorders.forEach((disorder) => {
    disordersFilter?.options?.push({
      label: disorder,
      value: disorder,
    });
  });

  const { data: roles } = useQuery({
    queryKey: ['admin-users-roles-search-filters', clinicLocationId],
    queryFn: () =>
      ClinicLocationService.loadProviderFilterData(clinicLocationId!),
    retry: 0,
    enabled: !!clinicLocationId,
  });

  const providerFilter = filters.find(
    (filter) => filter.fieldName === UserInfoFields.provider_id
  );

  roles?.forEach((provider) => {
    providerFilter?.options?.push({
      label: `${provider.first_name} ${provider.last_name}`,
      value: provider.id,
    });
  });

  return (
    <OvListFilter
      filters={filters}
      isOpened={true}
      hasAppliedFilters={hasAppliedListFilters(filters)}
      onRemoveFilters={removeFilters}
      onHandleFilterValueChange={onHandleFilterValueChange}
    />
  );
};

export default OvClinicLocationPatientSearchFilter;
